import React, {useReducer, useState} from "react";
import Table from "./components/FilingTable";
import {filingReducer, filingReducerInit, INITIAL_STATE} from "./utils/filingReducer";
import Container from "@amzn/awsui-components-react/polaris/container";
import {useLocation} from "react-router-dom";
import { statusColorCodes, TRANSACTION_DETAILS_TABLE_COLUMNS } from "../../common/utils/commonUtils";
import EllipsisText from "../../common/ellipsisText/EllipsisText";
import {formatDateWithTime} from "../../common/utils/apiUtils";
import { Alert, Button, ColumnLayout, Flashbar, Spinner } from "@amzn/awsui-components-react";
import {COLOR_RED, TRANSACTION_DETAIL_INFO, UNDERSCORE} from "../../common/constants/constants";
import {
    fetchCountry,
    getFileNameFromS3Key,
    getGeneratedReportName,
    hasSubmissionReceipt,
    hasTransactionInputFile,
    isTransactionInProgress,
    resolveTransactionErrorDetails
} from "./utils/Utils";
import {downloadFolder} from "../../common/utils/s3Utils";
import {getFilingReceipt} from "./utils/filingAPIs";
import {getGeneratedFileLabel, isTransformationRoute} from "./components/filingTableConfig";
import {downloadDocumentBys3PreSignedURL, downloadErrorReportByTransactionId} from "../excalibur/components/excaliburAPIs";
import {ERROR_REPORT_DOWNLOAD_FAILURE_FLASH, EXCALIBUR_STATUS} from "../excalibur/constants/constants";
import {DOWNLOAD_INITIAL_STATE, downloadReducer, downloadReducerInit} from "../exception/components/common/downloadDocumentReducer";
import AppConfig from "../../AppConfig";

function TransactionDetails(params) {
    const [filingData, filingDispatcher] = useReducer(filingReducer,
        INITIAL_STATE, filingReducerInit);
    const [downloadDocument, downloadDocumentDispatcher] = useReducer(downloadReducer,
        DOWNLOAD_INITIAL_STATE, downloadReducerInit);
    const [loading, setLoading] = useState(false);

    const location = useLocation();
    const passStateData = location?.state?.data;
    const flashBarItems = filingData?.flashBarItem ?? [];

    if (!passStateData?.transactionId) {
        return (
            <div className="transactionDetails">
                <Container>
                    <p style={{ color: COLOR_RED, fontWeight: "bold" }}>
                        Transaction ID is missing. Unable to display details.
                    </p>
                </Container>
            </div>
        );
    }

    const reportDownloadFailure = () => {
        filingDispatcher({
            type: "SET_FLASH_BAR", payload: {
                msg: 'Error occurred while downloading the file',
                type: 'error',
                dispatcher: filingDispatcher
            }
        });
    };

    const reportNoFileFound = () => {
        filingDispatcher({
            type: "SET_FLASH_BAR", payload: {
                msg: 'No file is available to download yet for this transaction',
                type: 'info',
                dispatcher: filingDispatcher
            }
        });
    };

    const downloadTransactionFolder = async (s3Key, s3Bucket) => {
        setLoading(true);
        try {
            const downloaded = await downloadFolder(s3Key, s3Bucket, params.domain);
            if (downloaded === 0) {
                reportNoFileFound();
            }
        } catch (error) {
            reportDownloadFailure();
        } finally {
            setLoading(false);
        }
    };

    const downloadTransactionInputFile = () =>
        downloadTransactionFolder(passStateData.inputFileKey, passStateData.inputFileBucket);

    // Same as the main filing page: read the receipt from S3 if it was written, otherwise fetch it
    // from the authority.
    const downloadTransactionReceipt = () => {
        if (passStateData.generatedFileKey) {
            downloadTransactionFolder(passStateData.generatedFileKey,
                AppConfig[params.domain].FILING_RECEIPT_BUCKET);
        } else {
            getFilingReceipt(filingDispatcher, passStateData.useCase, passStateData.subUseCase,
                passStateData.transactionId, params.domain, setLoading);
        }
    };

    // Same as the Excalibur table's Error Details column: a failed transformation's error report.
    const downloadErrorReport = async () => {
        setLoading(true);
        try {
            await downloadErrorReportByTransactionId({
                useCase: passStateData.useCase,
                subUseCase: passStateData.subUseCase,
                transactionId: passStateData.transactionId
            });
        } catch (error) {
            filingDispatcher({
                type: "SET_FLASH_BAR", payload: {
                    msg: ERROR_REPORT_DOWNLOAD_FAILURE_FLASH.message,
                    type: 'error',
                    dispatcher: filingDispatcher
                }
            });
        } finally {
            setLoading(false);
        }
    };

    // Same as the Excalibur table's Output File column: the generated report is a DMS document.
    // Failures are reported through downloadDocumentDispatcher's own flash bar.
    const downloadGeneratedReport = async () => {
        setLoading(true);
        try {
            await downloadDocumentBys3PreSignedURL(passStateData.documentId, passStateData.useCase,
                passStateData.subUseCase, downloadDocumentDispatcher);
        } finally {
            setLoading(false);
        }
    };

    const hasInputFile = hasTransactionInputFile(passStateData);
    const isTransformation = isTransformationRoute(location?.pathname);
    const hasGeneratedFile = isTransformation
        ? Boolean(passStateData.documentId)
        : hasSubmissionReceipt(passStateData);
    const downloadGeneratedFile = isTransformation ? downloadGeneratedReport : downloadTransactionReceipt;

    // A transformation's report name follows the Excalibur table; a filing's receipt is the stored
    // file, if any. A key that is a folder, or a receipt fetched on demand, has no name to show.
    const inputFileName = getFileNameFromS3Key(passStateData.inputFileKey);
    const generatedFileName = isTransformation
        ? getGeneratedReportName(passStateData)
        : getFileNameFromS3Key(passStateData.generatedFileKey);

    const errorDetailsText = resolveTransactionErrorDetails(passStateData);
    const hasErrorReport = isTransformation
        && passStateData.status === EXCALIBUR_STATUS.FAILURE
        && Boolean(passStateData.generatedFileKey);

    return (
        <>
            <div className="transactionDetails">
                {loading ? <Spinner className={"appLoader"} size="large" variant={"disabled"}>Loading</Spinner> : null}
                {/*
                  * The table's error paths report by dispatching SET_FLASH_BAR, so the page has to
                  * render what that produces or a failed download tells the user nothing. The
                  * reducer only wires onDismiss when it is also given a call-to-action handler,
                  * which these messages have none of, so dismissal is wired here instead.
                  */}
                {flashBarItems.length > 0 &&
                    <Flashbar items={flashBarItems.map(item => ({
                        ...item,
                        onDismiss: () => filingDispatcher({ type: "DISMISS_FLASHBAR", payload: {} })
                    }))} />
                }
                {downloadDocument?.flashBarItem?.length > 0 &&
                    <Flashbar items={downloadDocument.flashBarItem} />
                }
                {isTransactionInProgress(passStateData) &&
                    <Alert type="info" statusIconAriaLabel="Info">
                        This file is being processed. You will receive an email once it is complete.
                    </Alert>
                }
                <Container
                    header={
                        <h2>Transaction Details</h2>
                    }>
                    <Container>
                        <ColumnLayout columns={3}>
                            <div><b>Transaction ID:</b> {passStateData?.transactionId}</div>
                            <div><b>Country:</b> {fetchCountry(passStateData?.useCase) === "Compliance"
                                ? passStateData?.useCase
                                : fetchCountry(passStateData?.useCase)}</div>
                            <div><b>Filing Type:</b> {passStateData?.subUseCase}</div>
                        </ColumnLayout>
                        <ColumnLayout columns={3}>
                        <div><b>Request Created On:</b> {formatDateWithTime(passStateData?.startTimestamp)}</div>
                            <div><b>Status:</b> <span
                                style={statusColorCodes(passStateData?.status)}>{passStateData?.status}</span></div>
                            <div>
                                <b>Error Details:</b>{" "}
                                {errorDetailsText
                                    ? <span style={{ color: COLOR_RED }}>{errorDetailsText}</span>
                                    : "N/A"}
                                {hasErrorReport &&
                                    <>
                                        {" - "}
                                        <Button iconName="download" variant={"inline-link"} disabled={loading}
                                                onClick={downloadErrorReport}>Error Report</Button>
                                    </>
                                }
                            </div>
                        </ColumnLayout>
                        <ColumnLayout columns={3}>
                            <div>
                                <b>Input File:</b>{" "}
                                <Button iconName="download" variant={"inline-link"}
                                        disabled={!hasInputFile || loading}
                                        onClick={downloadTransactionInputFile}>
                                    <EllipsisText text={inputFileName} fallback="Download" />
                                </Button>
                            </div>
                            <div>
                                <b>{getGeneratedFileLabel(isTransformation)}:</b>{" "}
                                <Button iconName="download" variant={"inline-link"}
                                        disabled={!hasGeneratedFile || loading}
                                        onClick={downloadGeneratedFile}>
                                    <EllipsisText text={generatedFileName} fallback="Download" />
                                </Button>
                            </div>
                            <div><b>Requested By:</b> {passStateData?.createdBy}</div>
                        </ColumnLayout>
                    </Container>
                </Container>
                <div>
                <div className="table">
                    <Table
                            {...params}
                            table={filingData}
                            tableDispatcher={filingDispatcher}
                            tableHeader="Transaction Submission Summary"
                            visibleColumns={TRANSACTION_DETAILS_TABLE_COLUMNS}
                            transactionId={passStateData?.transactionId}
                            clientId={`${passStateData?.useCase}${UNDERSCORE}${passStateData?.subUseCase}`}
                            tableContext={TRANSACTION_DETAIL_INFO}
                        />
                    </div>
                </div>
            </div>
        </>
    )
}

export default TransactionDetails;
